// Local OUTBOX (IndexedDB): uploads never block the show. Queued, sent in the background with short timeouts, retried
// on landing / END / back online. post() resolves with the server reply of the first attempt, or { queued: true }.
const BASE = process.env.REACT_APP_BACKEND_URL;
const DB = 'spore-outbox';
const STORE = 'q';
const TIMEOUT_MS = 6000;
let dbp = null;
const db = () =>
  dbp ||
  (dbp = new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  }));
const op = async (mode, fn) => {
  const d = await db();
  return new Promise((res, rej) => {
    const tx = d.transaction(STORE, mode);
    const r = fn(tx.objectStore(STORE));
    tx.oncomplete = () => res(r && r.result);
    tx.onerror = () => rej(tx.error);
  });
};

const st = { count: 0, sent: 0, failed: 0, lastError: null, flushing: false };
let mem = []; // if IndexedDB is unavailable
const refresh = async () => {
  try {
    st.count = (await op('readonly', (s) => s.count())) + mem.length;
  } catch {
    st.count = mem.length;
  }
};

async function send(item) {
  const ac = new AbortController();
  const id = setTimeout(() => ac.abort(), TIMEOUT_MS);
  try {
    const r = await fetch(`${BASE}${item.path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: item.body, signal: ac.signal });
    if (r.status >= 500) throw new Error(`HTTP ${r.status}`);
    st.sent += 1;
    return r.ok ? r.json().catch(() => ({})) : {}; // 4xx: the server will never take it, drop it
  } finally {
    clearTimeout(id);
  }
}

export const outbox = {
  state: () => ({ ...st }),
  post(path, data) {
    const item = { path, body: JSON.stringify(data), at: new Date().toISOString() };
    return (async () => {
      let key = null;
      try {
        key = await op('readwrite', (s) => s.add(item));
      } catch {
        mem.push(item);
      }
      await refresh();
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return { queued: true };
      try {
        const reply = await send(item);
        if (key !== null) await op('readwrite', (s) => s.delete(key)).catch(() => {});
        else mem = mem.filter((x) => x !== item);
        await refresh();
        return reply;
      } catch (e) {
        st.failed += 1;
        st.lastError = String(e.message || e);
        return { queued: true };
      }
    })();
  },
  async flush() {
    if (st.flushing || (typeof navigator !== 'undefined' && navigator.onLine === false)) return st;
    st.flushing = true;
    try {
      const items = await op('readonly', (s) => s.getAll()).catch(() => []);
      for (const it of [...items, ...mem]) {
        try {
          await send(it);
          if (it.id !== undefined) await op('readwrite', (s) => s.delete(it.id));
          else mem = mem.filter((x) => x !== it);
        } catch (e) {
          st.lastError = String(e.message || e);
          break; // still offline / slow: try again later
        }
      }
    } finally {
      st.flushing = false;
      await refresh();
    }
    return st;
  },
};
export const clientLog = (rec) => outbox.post('/api/client-log', { ua: navigator.userAgent, at: new Date().toISOString(), ...rec });

if (typeof window !== 'undefined') {
  refresh();
  window.addEventListener('online', () => outbox.flush());
}
